import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { DisputeDto, DisputeCategory } from "@shared/types/DisputeDto";
import DisputeService from "@/services/DisputeService";
import { disputeService } from "@/services";
import { useAuthContext } from "./AuthProvider";

interface DisputeContextValue {
  disputes: DisputeDto[];
  loading: boolean;
  error: string | null;
  refreshDisputes: () => Promise<void>;
  submitDispute: (payload: SubmitDisputePayload) => Promise<void>;
  addComment: (disputeId: string, content: string, files?: File[]) => Promise<void>;
  acceptResolution: (disputeId: string) => Promise<void>;
  escalateToMediation: (disputeId: string) => Promise<void>;
}

interface SubmitDisputePayload {
  category: DisputeCategory;
  description: string;
  requestedOutcome: string;
  files?: File[];
}

const DisputeContext = createContext<DisputeContextValue | undefined>(
  undefined
);

export function DisputeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthContext();
  const [disputes, setDisputes] = useState<DisputeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDisputes = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      setError(null);
      const data = await disputeService.getDisputes();
      setDisputes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch disputes");
      console.error("Failed to fetch disputes:", err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDisputes();
    }
  }, [isAuthenticated, fetchDisputes]);

  const submitDispute = async (payload: SubmitDisputePayload) => {
    try {
      let images;
      if (payload.files && payload.files.length > 0) {
        images = await DisputeService.filesToBase64(payload.files);
      }
      await disputeService.submitDispute({
        category: payload.category,
        description: payload.description,
        requestedOutcome: payload.requestedOutcome,
        images,
      });
      await fetchDisputes();
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to submit dispute"
      );
    }
  };

  const addComment = async (disputeId: string, content: string, files?: File[]) => {
    try {
      let images;
      if (files && files.length > 0) {
        images = await DisputeService.filesToBase64(files);
      }
      await disputeService.addComment({ disputeId, content, images });
      await fetchDisputes();
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to add comment"
      );
    }
  };

  const acceptResolution = async (disputeId: string) => {
    try {
      await disputeService.acceptResolution(disputeId);
      await fetchDisputes();
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to accept resolution"
      );
    }
  };

  const escalateToMediation = async (disputeId: string) => {
    try {
      await disputeService.escalateToMediation(disputeId);
      await fetchDisputes();
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to escalate dispute"
      );
    }
  };

  return (
    <DisputeContext.Provider
      value={{
        disputes,
        loading,
        error,
        refreshDisputes: fetchDisputes,
        submitDispute,
        addComment,
        acceptResolution,
        escalateToMediation,
      }}
    >
      {children}
    </DisputeContext.Provider>
  );
}

export function useDisputes() {
  const context = useContext(DisputeContext);
  if (!context) {
    throw new Error("useDisputes must be used within DisputeProvider");
  }
  return context;
}
